/* oxlint-disable max-lines-per-function */
/* oxlint-disable @typescript-eslint/no-explicit-any */
import { type Action, type Middleware, type MiddlewareAPI, isAction } from '@reduxjs/toolkit';
import EventLoggerProvider from 'EventLogger/EventLoggerProvider';
import { type EventLogger } from 'EventLogger';
import deepMerge from 'Utils/deepMerge';
import { type EmptyObject, type AnyObject } from '@Types';
import { logTutorBotMessage, type BotUiContext } from 'TutorBotConversation';
import {
    type RealAiMessageSentEventExtraPayloadProperties,
    type ChatMessage,
    type RealAiMessage,
} from '../../types/ChatMessage.types';
import { isRealAiMessage, isStructuredMessage } from '../../utils/ChatMessage';
import { getSourceLocation } from '../../utils/sourceLocationHelpers';
import { chatActions } from './chatSlice';
import { getActiveConversationId, getActiveConversationUiContext, getMessageFromMessageId } from './selectors';

const { addMessage, mergeAiMessageAttrs } = chatActions;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const isActionWithPayload = (action: unknown): action is Action & { payload: any } =>
    isAction(action) && 'payload' in action;

const isNewMessage = (action: unknown): action is ReturnType<typeof addMessage> =>
    isActionWithPayload(action) && action.type === addMessage.type && !action.payload?.skipLogging;

const isCompletedMessage = (action: unknown): action is ReturnType<typeof mergeAiMessageAttrs> =>
    isActionWithPayload(action) &&
    action.type === mergeAiMessageAttrs.type &&
    !!action.payload?.attrs?.complete &&
    !action.payload?.attrs?.hasError;

const isErrorMessage = (
    action: unknown,
): action is ReturnType<typeof addMessage> | ReturnType<typeof mergeAiMessageAttrs> => {
    if (!isActionWithPayload(action)) return false;

    const isAddMessageError =
        action.type === chatActions.addMessage.type &&
        !!action.payload?.message?.hasError &&
        !action.payload?.skipLogging;

    const isMergeAttrsError = action.type === chatActions.mergeAiMessageAttrs.type && !!action.payload?.attrs?.hasError;

    return isAddMessageError || isMergeAttrsError;
};

const buildAiMessagePayload = ({
    message,
    aiMessageMetadata,
}: {
    message: ChatMessage;
    aiMessageMetadata: RealAiMessage['aiMessageMetadata'] | null | undefined;
}): RealAiMessageSentEventExtraPayloadProperties | EmptyObject => {
    if (!aiMessageMetadata) return {};
    const isRealAi = isRealAiMessage(message);
    const sources = isRealAi ? message.sources.map(getSourceLocation) : [];
    const conversationContext = isRealAi || isStructuredMessage(message) ? message.conversationContext : {};
    const canceled = isRealAi ? message.canceled : null;
    const hasError = isRealAi ? message.hasError : false;

    return {
        label: 'ai' as const,
        sender_role: 'ai' as const,
        ai_answer_sources: sources,
        bot_version: aiMessageMetadata.botVersion,
        algorithm: aiMessageMetadata.algorithm,
        total_tokens: aiMessageMetadata.totalTokens,
        total_tokens_from_cache: aiMessageMetadata.totalTokensFromCache,
        total_cost: aiMessageMetadata.totalCost,
        total_cost_from_cache: aiMessageMetadata.totalCostFromCache,
        prompt_tokens: aiMessageMetadata.promptTokens,
        prompt_tokens_from_cache: aiMessageMetadata.promptTokensFromCache,
        completion_tokens: aiMessageMetadata.completionTokens,
        completion_tokens_from_cache: aiMessageMetadata.completionTokensFromCache,
        seconds_to_first_token: aiMessageMetadata.secondsToFirstToken,
        seconds_to_completion: aiMessageMetadata.secondsToCompletion,
        conversation_context: conversationContext,
        canceled,
        hasError,
    };
};

const logMessage = (
    message: ChatMessage,
    activeConversationId: string,
    EventLogger: EventLogger,
    uiContext: BotUiContext,
) => {
    let extraPayloadProperties: RealAiMessageSentEventExtraPayloadProperties | EmptyObject = {};

    if (isRealAiMessage(message)) {
        extraPayloadProperties = buildAiMessagePayload({ message, aiMessageMetadata: message.aiMessageMetadata });
    } else if (isStructuredMessage(message)) {
        extraPayloadProperties = buildAiMessagePayload({ message, aiMessageMetadata: message.aiMessageMetadata });
    }

    // if the message was sent from the support tab
    // log a custom event for metrics tracking in statsig
    if ((message as AnyObject).supportTab) {
        EventLogger.log('tutorbot:message_sent_from_support_modal', {
            tutor_bot_message_id: message.id,
            tutor_bot_conversation_id: activeConversationId,
            sender_role: message.role,
        });
    }

    return logTutorBotMessage({ message, activeConversationId, uiContext, extraPayloadProperties });
};

export const logMessageMiddleware: Middleware =
    ({ getState }: MiddlewareAPI) =>
    next =>
    action => {
        const state = getState();
        const activeConversationId = getActiveConversationId(state);
        const uiContext = getActiveConversationUiContext(state);
        const EventLogger = EventLoggerProvider.get();

        if (!activeConversationId || !uiContext || !EventLogger) return next(action);

        if (isNewMessage(action)) {
            logMessage(action.payload.message, activeConversationId, EventLogger, uiContext);
        }

        if (isCompletedMessage(action)) {
            const { messageId, attrs = {} } = action.payload;

            const message = getMessageFromMessageId(state, messageId);
            logMessage(deepMerge(message, attrs), activeConversationId, EventLogger, uiContext);
        }

        if (isErrorMessage(action)) {
            if (action.type === chatActions.addMessage.type) {
                logMessage(action.payload.message, activeConversationId, EventLogger, uiContext);
            } else {
                const { messageId, attrs = {} } = action.payload;

                const message = getMessageFromMessageId(state, messageId);
                logMessage(deepMerge(message, attrs), activeConversationId, EventLogger, uiContext);
            }
        }

        return next(action);
    };
